import React, { useState, useRef, useEffect, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { FaPlus } from "react-icons/fa6";
import {
  BsThreeDotsVertical,
  BsSortAlphaDown,
  BsSortAlphaDownAlt,
} from "react-icons/bs";
import { IoSearch } from "react-icons/io5";
import { MdDeleteForever } from "react-icons/md";

const ORG_COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#3b82f6",
  "#ef4444",
  "#a855f7",
  "#f97316",
  "#06b6d4",
];

const getOrgColor = (index) => ORG_COLORS[index % ORG_COLORS.length];
const getOrgInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/**
 * Left column — "All Organizations" panel.
 *
 * All org-list-specific state (search, author filter, sort order, context menu)
 * lives here so a keypress in the search box does NOT re-render FolderTreePanel
 * or FileDetailPanel.
 */
const OrgListPanel = React.memo(
  ({
    activeOrgs,
    selectedOrg,
    onSelectOrg,
    effectivePermissions,
    user,
    orgsLoading,
    onAddOrg,
    onEditOrg,
    onDeleteOrg,
    onManageMembers,
  }) => {
    // ── Local state ───────────────────────────────────────────────
    const [orgSearch, setOrgSearch] = useState("");
    const [authorFilter, setAuthorFilter] = useState("");
    const [orgContextMenuId, setOrgContextMenuId] = useState(null);
    const [orgContextMenuPos, setOrgContextMenuPos] = useState({
      top: 0,
      left: 0,
      bottom: "auto",
    });
    const [orgSortOrder, setOrgSortOrder] = useState("default"); // 'default' | 'asc' | 'desc'

    const orgContextMenuRef = useRef(null);

    // Close context menu on outside click
    useEffect(() => {
      const close = (e) => {
        if (e.target.closest("button")) return;
        if (
          orgContextMenuRef.current &&
          !orgContextMenuRef.current.contains(e.target)
        ) {
          setOrgContextMenuId(null);
        }
      };
      document.addEventListener("click", close);
      return () => document.removeEventListener("click", close);
    }, []);

    // ── Derived data ──────────────────────────────────────────────
    const authorOptions = useMemo(() => {
      const names = new Set();
      activeOrgs.forEach((o) => {
        if (o.createdUserName) names.add(o.createdUserName);
      });
      return [...names].sort((a, b) => a.localeCompare(b));
    }, [activeOrgs]);

    const filteredOrgs = useMemo(() => {
      const filtered = activeOrgs.filter((o) => {
        const matchesSearch = (o.organizationsName || "")
          .toLowerCase()
          .includes(orgSearch.toLowerCase());
        const matchesAuthor =
          !authorFilter || o.createdUserName === authorFilter;
        return matchesSearch && matchesAuthor;
      });
      if (orgSortOrder === "asc")
        return [...filtered].sort((a, b) =>
          (a.organizationsName || "").localeCompare(b.organizationsName || "")
        );
      if (orgSortOrder === "desc")
        return [...filtered].sort((a, b) =>
          (b.organizationsName || "").localeCompare(a.organizationsName || "")
        );
      return filtered;
    }, [activeOrgs, orgSearch, authorFilter, orgSortOrder]);

    // ── Render ────────────────────────────────────────────────────
    return (
      <div className="w-[280px] flex-shrink-0 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800 tracking-wide">
            All Organizations
          </h3>
          <div className="flex items-center gap-1">
            {effectivePermissions.isEdit && (
              <button
                data-tooltip-id="toolbar-tooltip"
                data-tooltip-content="Add Organization"
                onClick={onAddOrg}
                className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
              >
                <FaPlus size={13} />
              </button>
            )}
            <button
              data-tooltip-id="toolbar-tooltip"
              data-tooltip-content={
                orgSortOrder === "default"
                  ? "Sort A-Z"
                  : orgSortOrder === "asc"
                    ? "Sort Z-A"
                    : "Default Order"
              }
              onClick={() =>
                setOrgSortOrder((prev) =>
                  prev === "default"
                    ? "asc"
                    : prev === "asc"
                      ? "desc"
                      : "default"
                )
              }
              className="flex items-center p-1.5 text-xs font-semibold rounded-full transition-colors text-white bg-[#4338ca]"
            >
              {orgSortOrder === "desc" ? (
                <BsSortAlphaDownAlt size={16} />
              ) : (
                <BsSortAlphaDown size={16} />
              )}
            </button>
          </div>
        </div>

        {/* Search + Author Filter */}
        <div className="px-3 py-2.5 border-b border-slate-50 space-y-2">
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5">
            <IoSearch size={12} className="text-slate-400 shrink-0" />
            <input
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              placeholder="Search organizations..."
              className="bg-transparent text-xs text-slate-700 placeholder-slate-400 flex-1 outline-none min-w-0"
            />
          </div>
          {authorOptions.length > 1 && (
            <select
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              className="w-full border border-slate-300 bg-slate-50 rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 cursor-pointer font-medium"
            >
              <option value="">All Authors</option>
              {authorOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Org List */}
        <div className="flex-1 overflow-y-auto py-1.5 scroll-inner">
          {orgsLoading ? (
            <div className="space-y-3 px-3 py-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                  <Skeleton circle width={32} height={32} />
                  <div className="flex-1">
                    <Skeleton width="80%" height={14} />
                    <Skeleton width="50%" height={10} />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredOrgs.length === 0 ? (
            <p className="text-center text-xs text-slate-400 py-8">
              No organizations found
            </p>
          ) : (
            filteredOrgs.map((org, idx) => (
              <div
                key={org.organizationsDNSId}
                onClick={() => onSelectOrg(org)}
                className={`group flex items-center gap-3 px-3 py-2.5 mx-2 my-0.5 rounded-xl cursor-pointer transition-colors ${selectedOrg?.organizationsDNSId === org.organizationsDNSId
                  ? "bg-indigo-50 shadow-sm"
                  : "hover:bg-slate-50"
                  }`}
              >
                {/* Avatar */}
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow-sm"
                  style={{ backgroundColor: getOrgColor(idx) }}
                >
                  {getOrgInitials(org.organizationsName)}
                </div>

                {/* Name + item count */}
                <div className="min-w-0 flex-1">
                  <p
                    data-tooltip-id="toolbar-tooltip"
                    data-tooltip-content={org.organizationsName}
                    className={`truncate ${selectedOrg?.organizationsDNSId === org.organizationsDNSId
                      ? "text-[15px] font-bold text-indigo-700"
                      : "text-sm font-semibold text-slate-800"
                      }`}
                  >
                    {org.organizationsName}
                  </p>
                  <p
                    className={`mt-0.5 font-medium ${selectedOrg?.organizationsDNSId === org.organizationsDNSId
                      ? "text-xs text-indigo-400"
                      : "text-[11px] text-slate-400"
                      }`}
                  >
                    {org?.totalItem ?? "0"} Items
                  </p>
                </div>

                {/* Three-dot context menu */}
                {(effectivePermissions.isEdit ||
                  effectivePermissions.isDelete) && (
                    <div
                      className="relative"
                      ref={
                        orgContextMenuId === org.organizationsDNSId
                          ? orgContextMenuRef
                          : null
                      }
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (orgContextMenuId === org.organizationsDNSId) {
                            setOrgContextMenuId(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setOrgContextMenuId(org.organizationsDNSId);
                            const estimatedMenuHeight = 200;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            if (spaceBelow < estimatedMenuHeight) {
                              setOrgContextMenuPos({
                                top: "auto",
                                bottom: window.innerHeight - rect.top + 4,
                                left: rect.left,
                              });
                            } else {
                              setOrgContextMenuPos({
                                top: rect.bottom + 4,
                                bottom: "auto",
                                left: rect.left,
                              });
                            }
                          }
                        }}
                        className={`p-1.5 rounded-lg transition-all ${orgContextMenuId === org.organizationsDNSId
                          ? "bg-indigo-50 text-indigo-600"
                          : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          }`}
                      >
                        <BsThreeDotsVertical size={13} />
                      </button>

                      {orgContextMenuId === org.organizationsDNSId && (
                        <div
                          className="fixed z-[100] w-48 bg-white border border-slate-100 rounded-2xl shadow-2xl py-2 overflow-hidden"
                          style={{
                            top: orgContextMenuPos.top,
                            bottom: orgContextMenuPos.bottom,
                            left: orgContextMenuPos.left,
                            boxShadow:
                              "0 10px 40px -5px rgba(0,0,0,0.15), 0 4px 16px -4px rgba(0,0,0,0.1)",
                          }}
                        >
                          {effectivePermissions.isEdit && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditOrg(org);
                                  setOrgContextMenuId(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                              >
                                <span className="text-sm">✏️</span>
                                <span>Edit</span>
                              </button>
                              {user?.parentID === 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onManageMembers(org);
                                    setOrgContextMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                >
                                  <span className="text-slate-500">👥</span>
                                  <span>Manage Members</span>
                                </button>
                              )}
                            </>
                          )}
                          {effectivePermissions.isDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteOrg(org);
                                setOrgContextMenuId(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-red-500 hover:bg-red-50 transition-colors text-left font-medium"
                            >
                              <MdDeleteForever size={18} />
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 bg-white mt-auto rounded-b-2xl">
          <p className="text-xs text-slate-500 font-medium text-center">
            Total Organizations:{" "}
            <span className="font-bold text-indigo-600">
              {activeOrgs.length}
            </span>
          </p>
        </div>
      </div>
    );
  }
);

OrgListPanel.displayName = "OrgListPanel";
export default OrgListPanel;
